import { ClvrScreenControlCellComponentsInterface } from '../interface'
import { ClvrScreenControlHeadersLabelsEnum } from '../enum'
import { mappingEndOperation, SC_PRIMARY_COLOR } from '../const'
import Operation from '../../../../../dPplanningOp/vue/Classes/ClvrOperation'
import Staff from '../../../../../dPpersonnel/vue/Classes/Staff'
import { EnumStaffRole } from '../../../../../dPpersonnel/vue/Enum'

export const DEFAULT_COMPONENT = 'ClvrScreenControlDefaultCell'
export const DEFAULT_PROP = { propName: 'text' }

type StaffRole = {
    component: string
    dataRelationProps: {
        [key: string]: {
            propName: string
            customMapping: (data: Operation) => unknown
        }
    }
    classNameCellHeader: string[]
    classNameCellBody: string[]
}

type StaffRoleAvatar = {
    component: string
    dataRelationProps: {
        [key: string]: {
            propName: string
            customMapping: (data: Operation) => unknown
        }
    }
    classNameCellHeader: string[]
    classNameCellBody: string[]
}

export const createStaffRole = (
    headerLabelEnum: ClvrScreenControlHeadersLabelsEnum,
    staffRoleEnum: EnumStaffRole,
): StaffRole => {
    return {
        component: 'ClvrScreenControlOperatingRoomStaff',
        dataRelationProps: {
            users: {
                propName: 'users',
                customMapping: (data: Operation) => Staff.getUserWithRoleFromStaff(data.staffs, staffRoleEnum),
            },
            useAvatar: {
                propName: 'useAvatar',
                customMapping: () => false,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--personal'],
        classNameCellBody: ['screen-control-planning-table__avatar'],
    }
}

export const createStaffRoleAvatar = (
    headerLabelEnum: ClvrScreenControlHeadersLabelsEnum,
    staffRoleEnum: EnumStaffRole,
): StaffRoleAvatar => {
    return {
        component: 'ClvrScreenControlOperatingRoomStaff',
        dataRelationProps: {
            users: {
                propName: 'users',
                customMapping: (data: Operation) =>
                    Staff.getUserWithRoleFromStaff(data.staffs, staffRoleEnum).map(user => {
                        user.color = user.color ?? SC_PRIMARY_COLOR ?? ''
                        return user
                    }),
            },
            useAvatar: {
                propName: 'useAvatar',
                customMapping: () => true,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--personal'],
        classNameCellBody: ['screen-control-planning-table__avatar'],
    }
}

export const CELL_COMPONENTS_MAPPING: Record<string, ClvrScreenControlCellComponentsInterface> = {
    [ClvrScreenControlHeadersLabelsEnum.INTERVENTION_STATUS]: {
        component: 'ClvrScreenControlInterventionStatus',
        dataRelationProps: {
            status: {
                propName: 'status',
                customMapping: (data: Operation) => data.operationTimeStatus,
            },
            emergency: {
                propName: 'emergency',
                customMapping: (data: Operation) => data.emergency,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--status'],
        classNameCellBody: ['screen-control-planning-table__body-cell--status'],
    },
    [ClvrScreenControlHeadersLabelsEnum.HOURLY]: {
        component: 'ClvrScreenControlInterventionScheduleCell',
        dataRelationProps: {
            [ClvrScreenControlHeadersLabelsEnum.START_HOURLY]: {
                propName: 'start',
                customMapping: (data: Operation) => data.startOperation ?? '',
            },
            [ClvrScreenControlHeadersLabelsEnum.END_HOURLY]: {
                propName: 'end',
                customMapping: (data: Operation) => mappingEndOperation(data),
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--hourly'],
        classNameCellBody: ['screen-control-planning-table__body-cell--hourly'],
    },
    [ClvrScreenControlHeadersLabelsEnum.INTERVENTION]: {
        component: 'ClvrScreenControlInterventionCell',
        dataRelationProps: {
            [ClvrScreenControlHeadersLabelsEnum.INTERVENTION]: {
                customMapping: (data: Operation) => data.label,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--intervention'],
        classNameCellBody: ['screen-control-planning-table__body-cell--intervention'],
    },
    [ClvrScreenControlHeadersLabelsEnum.MATERIAL_AND_REMARK]: {
        component: 'ClvrScreenControlMaterialAndRemarkNoteCell',
        dataRelationProps: {
            [ClvrScreenControlHeadersLabelsEnum.MATERIAL]: {
                propName: 'materials',
                customMapping: (data: Operation) => data.materials,
            },
            [ClvrScreenControlHeadersLabelsEnum.MATERIAL_LIST]: {
                propName: 'additionalMaterials',
                customMapping: (data: Operation) => data.additionalMaterials,
            },
            [ClvrScreenControlHeadersLabelsEnum.REMARKS]: {
                propName: 'remarks',
                customMapping: (data: Operation) => data.remarks,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--equipment-remarks'],
        classNameCellBody: ['screen-control-planning-table__body-cell--equipment-remarks'],
    },
    [ClvrScreenControlHeadersLabelsEnum.INTERVENTION_AND_MATERIAL]: {
        component: 'ClvrScreenControlInterventionMaterialCell',
        dataRelationProps: {
            [ClvrScreenControlHeadersLabelsEnum.INTERVENTION]: {
                propName: 'intervention',
                customMapping: (data: Operation) => data.label,
            },
            [ClvrScreenControlHeadersLabelsEnum.INTERVENTION_AND_MATERIAL_LIST]: {
                propName: 'materialList',
                customMapping: (data: Operation) => data.additionalMaterials,
            },
            [ClvrScreenControlHeadersLabelsEnum.INTERVENTION_AND_MATERIAL_TO_PROVIDE]: {
                propName: 'materialToProvide',
                customMapping: (data: Operation) => data.materials,
            },
            [ClvrScreenControlHeadersLabelsEnum.INTERVENTION_AND_REMARKS]: {
                propName: 'remarks',
                customMapping: (data: Operation) => data.remarks,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--intervention-material'],
        classNameCellBody: ['screen-control-planning-table__body-cell--intervention-material'],
    },
    [ClvrScreenControlHeadersLabelsEnum.TYPE_OF_ANESTHESIA]: {
        dataRelationProps: {
            [ClvrScreenControlHeadersLabelsEnum.TYPE_OF_ANESTHESIA]: {
                customMapping: (data: Operation) => data.anesthesiaType?.name,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--anesthesia-type'],
        classNameCellBody: ['screen-control-planning-table__body-cell--anesthesia-type'],
    },
    [ClvrScreenControlHeadersLabelsEnum.PATIENT]: {
        component: 'ClvrScreenControlPatientCell',
        dataRelationProps: {
            [ClvrScreenControlHeadersLabelsEnum.PATIENT_FIRSTNAME]: {
                propName: 'firstName',
                customMapping: (data: Operation) => data.patient?.firstName,
            },
            [ClvrScreenControlHeadersLabelsEnum.PATIENT_LASTNAME]: {
                propName: 'lastName',
                customMapping: (data: Operation) => data.patient?.name,
            },
            [ClvrScreenControlHeadersLabelsEnum.AGE_PATIENT]: {
                propName: 'age',
                customMapping: (data: Operation) => data.patient?.age,
            },
            [ClvrScreenControlHeadersLabelsEnum.ADMISSION_TYPE_PATIENT]: {
                propName: 'admission',
                customMapping: (data: Operation) => data.stay?.admissionType?.label,
            },
            [ClvrScreenControlHeadersLabelsEnum.NB_ROOM_PATIENT]: {
                propName: 'room',
                customMapping: (data: Operation) => data.stay?.currentAffectation?.bed?.hospitalRoom?.name,
            },
            [ClvrScreenControlHeadersLabelsEnum.SERVICE_PATIENT]: {
                propName: 'service',
                customMapping: (data: Operation) => data.stay?.currentAffectation?.service?.name,
            },
            sex: {
                propName: 'sex',
                customMapping: (data: Operation) => data.patient?.sex,
            },
            cancelled: {
                propName: 'cancelledOperation',
                customMapping: (data: Operation) => data.cancelled,
            },
            [ClvrScreenControlHeadersLabelsEnum.ISOLATION_PATIENT_TYPE]: {
                propName: 'isolationType',
                customMapping: (data: Operation) => data.stay?.isolationType,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--patient'],
        classNameCellBody: ['screen-control-planning-table__body-cell--patient'],
    },
    [ClvrScreenControlHeadersLabelsEnum.SURGEON]: {
        dataRelationProps: {
            [ClvrScreenControlHeadersLabelsEnum.SURGEON]: {
                customMapping: (data: Operation) => data.surgeon?.fullNameWithInitial,
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--practitioner'],
        classNameCellBody: ['screen-control-planning-table__body-cell--practitioner'],
    },
    [ClvrScreenControlHeadersLabelsEnum.ANESTHESIA]: {
        dataRelationProps: {
            [ClvrScreenControlHeadersLabelsEnum.ANESTHESIA]: {
                customMapping: (data: Operation) => data.getAnesthetistForScreenControlToString(),
            },
        },
        classNameCellHeader: ['screen-control-planning-table__header-cell--practitioner'],
        classNameCellBody: ['screen-control-planning-table__body-cell--practitioner'],
    },
    [ClvrScreenControlHeadersLabelsEnum.NURSE_INSTRUMENTALIST]: createStaffRole(
        ClvrScreenControlHeadersLabelsEnum.NURSE_INSTRUMENTALIST,
        EnumStaffRole.INSTRUMENTALIST,
    ),
    [ClvrScreenControlHeadersLabelsEnum.NURSE_ANESTHETIST]: createStaffRole(
        ClvrScreenControlHeadersLabelsEnum.NURSE_ANESTHETIST,
        EnumStaffRole.NURSE_ANESTHETIST,
    ),
    [ClvrScreenControlHeadersLabelsEnum.NURSE_ROOM_HELPERS]: createStaffRole(
        ClvrScreenControlHeadersLabelsEnum.NURSE_ROOM_HELPERS,
        EnumStaffRole.ROOM_HELPER,
    ),
    [ClvrScreenControlHeadersLabelsEnum.NURSE_INSTRUMENTALIST_AVATAR]: createStaffRoleAvatar(
        ClvrScreenControlHeadersLabelsEnum.NURSE_INSTRUMENTALIST_AVATAR,
        EnumStaffRole.INSTRUMENTALIST,
    ),
    [ClvrScreenControlHeadersLabelsEnum.NURSE_ANESTHETIST_AVATAR]: createStaffRoleAvatar(
        ClvrScreenControlHeadersLabelsEnum.NURSE_ANESTHETIST_AVATAR,
        EnumStaffRole.NURSE_ANESTHETIST,
    ),
    [ClvrScreenControlHeadersLabelsEnum.NURSE_ROOM_HELPERS_AVATAR]: createStaffRoleAvatar(
        ClvrScreenControlHeadersLabelsEnum.NURSE_ROOM_HELPERS_AVATAR,
        EnumStaffRole.ROOM_HELPER,
    ),
}
